import { logisticsApi } from "@/api/ApiClient";
import { useQuery } from "@tanstack/react-query";
import { employee, employees, type IEmployee } from "@medoma/models/shifts";
import { useMemo } from "react";
import { z } from "zod";
import { useUserProfile } from "./Users";
import {
  informationFieldConfigValueSchema,
  usePatientInformationFields,
} from "./Patients";

export const fetchEmployee = async (employeeNumber: number) => {
  const parsedEmployee = await logisticsApi
    .get(`/employees/${employeeNumber}`)
    .json(employee);
  return parsedEmployee;
};

const employeeOtpSchema = z.object({
  code: z.string(),
});

export const generateEmployeeOtp = async (employeeNumber: string) => {
  const parsedOtp = (
    await logisticsApi
      .post(`/employees/${employeeNumber}/otp`)
      .json(employeeOtpSchema)
  ).code;
  return parsedOtp;
};

export const logoutEmployee = async (employeeNumber: string) => {
  await logisticsApi.post(`/employees/${employeeNumber}/logout`);
};

async function fetchEmployees() {
  const parsedEmployees = await logisticsApi.get("/employees").json(employees);
  return parsedEmployees.sort((a, b) => a.firstName.localeCompare(b.firstName));
}

const employeeKeys = {
  all: ["employees"] as const,
  lists: () => [...employeeKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...employeeKeys.lists(), { filters }] as const,
  detail: (id: number) => [...employeeKeys.all, id, "details"] as const,
};

export const useCurrentEmployee = () => {
  const { employeeNumber } = useUserProfile();
  const employeeNumberAsNumber = Number(employeeNumber);
  return useQuery({
    queryKey: employeeKeys.detail(employeeNumberAsNumber),
    queryFn: () => fetchEmployee(employeeNumberAsNumber),
  });
};

export const useEmployee = (employeeNumber: number) => {
  return useQuery({
    queryKey: employeeKeys.detail(employeeNumber),
    queryFn: () => fetchEmployee(employeeNumber),
  });
};

export const useEmployees = () => {
  return useQuery({
    queryKey: employeeKeys.lists(),
    queryFn: fetchEmployees,
  });
};

export const usePrimaryResponsibleEmployees = () => {
  const { data: patientInformationFields } = usePatientInformationFields();
  const isPrimaryResponsibleHidden =
    patientInformationFields?.PrimaryResponsible ===
    informationFieldConfigValueSchema.enum.hidden;

  const { data: employees } = useQuery({
    queryKey: employeeKeys.lists(),
    queryFn: fetchEmployees,
    enabled:
      patientInformationFields !== undefined && !isPrimaryResponsibleHidden,
  });

  const employeesMap = useMemo(() => {
    const map = new Map<number, IEmployee>();
    employees?.forEach((employee) => {
      map.set(employee.id, employee);
    });
    return map;
  }, [employees]);

  return { employeesMap, isPrimaryResponsibleHidden };
};
